import { createUID } from '../services/util';

export enum AnnotationType {
    // a vertical line at a specific x-value
    LINE_X = 'line-x',
    // a horizontal line at a specific y-value
    LINE_Y = 'line-y',
    // a line segment that starts at an xy-value and ends at another xy-value
    LINE_SEGMENT = 'line-segment',
    // a highlighted area across a range of x-values
    AREA_X = 'area-x',
    // a highlighted area across both a range of x-values and a range of y-values
    AREA_XY = 'area-xy',
    //
    ADD_TEXT_BUBBLE = 'add-text-bubble'
}

export enum LineType {
    DOTTED = 'line-style-dotted',
    DASHED = 'line-style-dashed',
    SOLID = 'line-style-solid'
}

export interface IAnnotationPoint {
    x: number;
    y: number;
    xAxis?: number;
    yAxis?: number;
}

export interface IAnnotationColor {
    color: string;
    class: string;
}

export const NEUTRAL_COLOR: IAnnotationColor = {
    color: 'a',
    class: 'a'
};

export const COLORS: IAnnotationColor[] = [ {
    class: 'magenta',
    color: '#ff63e1'
}, {
    class: 'red',
    color: '#f04444'
}, {
    class: 'orange',
    color: '#ff992c'
}, {
    class: 'yellow',
    color: '#ddf626'
}, {
    class: 'green',
    color: '#32ff36'
}, {
    class: 'cyan',
    color: '#35ddff'
} ];

// when setting point x/y values, we can anchor the values either to an x-axis/y-axis value,
// or to chart pixel coordinates (the upper-left corner of the chart is 0,0 ).
// to get vertical lines and areas, we anchor the x-values to the x-axis, and set y-values
// according to chart pixel coordinates. To get a vertical line that spans the whole chart,
// we need to set some high number; highcharts doesn't like MAX_SAFE_INTEGER, so we use some
// arbitrarily high value, which will be greater than the pixel height or width of any chart on the screen.
const MAX = 10000;

export abstract class Annotation {
    type: AnnotationType;
    selected: boolean;
    id: string;
    points: IAnnotationPoint[];

    constructor( public className: string, public color: IAnnotationColor ) {
        this.id = createUID();
    }

    /** return the proper config for a highstock annotation */
    getHighstockOptions(): {} {
        return {
            draggable: '',
            id: this.id, // id is required when removing annotations
            shapes: [ {
                className: this.className + ' palette-color-' + this.color.class,
                type: 'path',
                points: this.points
            } ],
            zIndex: this.type === AnnotationType.AREA_X ? 1 : 10 // keep all annotations (except for area annotations) drawn over the data
        };
    }
}

export class VerticalLineAnnotation extends Annotation {
    type = AnnotationType.LINE_X;

    constructor( className: string, color: IAnnotationColor, x: number ) {
        super( className, color );
        this.points = [
            { x: x, y: 0,   xAxis: 0 },
            { x: x, y: MAX, xAxis: 0 }
        ];
    }
}

export class HorizontalLineAnnotation extends Annotation {
    type = AnnotationType.LINE_Y;

    constructor( className: string, color: IAnnotationColor, y: number ) {
        super( className, color );
        this.points = [
            { x: 0,   y: y, yAxis: 0 },
            { x: MAX, y: y, yAxis: 0 }
        ];
    }
}

export class LineSegmentAnnotation extends Annotation {
    type = AnnotationType.LINE_SEGMENT;

    constructor( className: string, color: IAnnotationColor, x1: number, x2: number, y1: number, y2: number ) {
        super( className, color );
        // ensure x1,y1 is to the left of x2,y2, which makes some calculations easier
        if ( x1 > x2 ) {
            const tmpX = x2;
            const tmpY = y2;
            x2 = x1;
            y2 = y1;
            x1 = tmpX;
            y1 = tmpY;
        }
        this.points = [
            { x: x1, y: y1, xAxis: 0, yAxis: 0 },
            { x: x2, y: y2, xAxis: 0, yAxis: 0 }
        ];
    }
}

export class HighlightAnnotation extends Annotation {
    type = AnnotationType.AREA_X;

    constructor( color: IAnnotationColor, x1: number, x2: number ) {
        super( 'type-area', color );
        this.points = [
            { x: Math.min( x1, x2 ), y: 0,   xAxis: 0 },
            { x: Math.max( x1, x2 ), y: 0,   xAxis: 0 },
            { x: Math.max( x1, x2 ), y: MAX, xAxis: 0 },
            { x: Math.min( x1, x2 ), y: MAX, xAxis: 0 }
        ];
    }
}

export class TextBubbleAnnotation extends Annotation {
    type = AnnotationType.ADD_TEXT_BUBBLE;

    constructor( public text: string, x: number, y: number ) {
        super( '', NEUTRAL_COLOR );
        this.points = [ {
            x: x,
            y: y,
            xAxis: 0,
            yAxis: 0
        } ];
    }

    getHighstockOptions() {
        return {
            draggable: '',
            id: this.id, // id is required when removing annotations
            labels: [ {
                point: this.points[0],
                text: this.text,
                y: -10,
                allowOverlap: true
            } ]
        };
    }
}
